import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';
import ReactDOMServer from 'react-dom/server';

import {colors} from '../colors/colors';
import {HoverPopover} from '../hover-popover/hover-popover';
import {GoalLeadInfoIcon, WarningIcon, ErrorCircleIcon, ConfirmIcon, LockIcon} from '../icon';
import {Body} from '../typography/body';
import type {TextSize} from '../typography/body';

import styles from './status-badge.module.css';

export type Level =
    | 'info'
    | 'info--purple'
    | 'info--navy'
    | 'info--grey'
    | 'warning'
    | 'warning--grey'
    | 'alert'
    | 'alert--text'
    | 'success'
    | 'hidden';

export type StatusBadgeProps = {
    level: Level;
    text: string;
    detail?: string | React.ReactNode;
    detailMaxWidth?: number;
    includeIcon?: boolean;
    customIcon?: any;
    isInteractive?: boolean;
    size?: TextSize;
    smallIcon?: boolean;
    badgeWidth?: string;
    onClick?: (event: React.SyntheticEvent<HTMLElement>) => void;
    getRef?: (ref?: HTMLElement | null | undefined) => void;
};

type Props = StatusBadgeProps;

export function StatusBadge(props: Props) {
    const {
        includeIcon,
        level,
        detail,
        text,
        size = 'normal',
        isInteractive,
        badgeWidth,
        customIcon,
        onClick,
    } = props;

    const statusBadgeRef = React.useRef(null);

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, []);

    const statusStyleName = classnames({
        [styles['status-badge']]: true,
        [styles['status-badge--interactive']]: isInteractive,
        [styles['status-badge--clickable']]: onClick,
        [styles['status-badge--warning']]: level === 'warning',
        [styles['status-badge--alert']]: level === 'alert',
        [styles['status-badge--alert-text']]: level === 'alert--text',
        [styles['status-badge--info']]: level === 'info',
        [styles['status-badge--success']]: level === 'success',
        [styles['status-badge--hidden']]: level === 'hidden',
        [styles['status-badge--info-purple']]: level === 'info--purple',
        [styles['status-badge--info-navy']]: level === 'info--navy',
        [styles['status-badge--info-grey']]: level === 'info--grey' || level === 'warning--grey',
    });

    let icon = null;

    if (includeIcon) {
        const Icon = getIcon(level);
        icon = (
            <Icon wrapWithDiv={false} size={props.smallIcon ? 12 : 15} fill={getFillColor(level)} />
        );
    }

    if (customIcon) {
        const Icon = customIcon;
        icon = <Icon wrapWithDiv={false} size={props.smallIcon ? 12 : 15} />;
    }

    let reactTooltipProps: Record<string, any> = {};
    let detailHoverPopover;
    if (detail) {
        switch (typeof detail) {
            case 'string':
                reactTooltipProps = {
                    ...(props.detailMaxWidth
                        ? {
                              'data-html': true,
                              'data-tip': ReactDOMServer.renderToString(
                                  <div style={{maxWidth: props.detailMaxWidth}}>{detail}</div>
                              ),
                          }
                        : {'data-tip': detail}),
                    'data-effect': 'solid',
                    'data-delay-show': '2',
                    'data-event': 'mouseenter focus',
                    'data-event-off': 'mouseleave blur click',
                };
                break;
            case 'object':
                detailHoverPopover = (
                    // @ts-expect-error
                    <HoverPopover
                        anchorRef={statusBadgeRef}
                        location='top'
                        bodyColor='dark'
                        customStyles={{maxWidth: props.detailMaxWidth}}
                    >
                        {detail}
                    </HoverPopover>
                );
                break;
        }
    }

    const Component = onClick ? 'button' : 'div';

    return (
        <>
            <Component
                tabIndex={onClick ? 0 : undefined}
                // @ts-expect-error
                ref={(ref) => {
                    statusBadgeRef.current = ref;
                    props.getRef?.(ref);
                }}
                style={{width: badgeWidth}}
                onClick={
                    onClick
                        ? (e) => {
                              e.preventDefault();
                              if (onClick) {
                                  onClick(e);
                              }
                          }
                        : undefined
                }
                type={onClick ? 'button' : undefined}
                {...reactTooltipProps}
                className={statusStyleName}
            >
                {icon}
                {text && (
                    <Body size={size} color='inherit'>
                        {text}
                    </Body>
                )}
            </Component>
            {detailHoverPopover}
        </>
    );
}

export function getFillColor(level: Level) {
    switch (level) {
        case 'info':
            return 'hsla(195, 71%, 37%, 1)';
        case 'warning--grey':
        case 'warning':
            return 'hsla(20, 81%, 56%, 1)';
        case 'alert':
        case 'alert--text':
            return 'hsla(360, 95%, 64%, 1)';
        case 'success':
            return 'hsla(145, 71%, 26%, 1)';
        case 'hidden':
            return colors.greyDk;
        default:
            return colors.grey;
    }
}

function getIcon(level: Level) {
    switch (level) {
        case 'info':
            return GoalLeadInfoIcon;
        case 'warning':
        case 'warning--grey':
            return WarningIcon;
        case 'alert':
        case 'alert--text':
            return ErrorCircleIcon;
        case 'success':
            return ConfirmIcon;
        case 'hidden':
            return LockIcon;
        default:
            return GoalLeadInfoIcon;
    }
}
